import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";

const CentresService = (function () {
    // Private data cache
    let data = [];
    let isListenerRegistered = false;
    let isLoading = false; // Add loading flag to prevent duplicate requests

    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    }

    function updateCentre(updatedRecord) {
        try {
            if (!updatedRecord || !updatedRecord.id) {
                logger.warn('Invalid centre record received for update:', updatedRecord);
                return;
            }

            const existingRecordIndex = data.findIndex(record => record.id === updatedRecord.id);

            if (existingRecordIndex !== -1) {
                // Update existing record in place to maintain array references
                data[existingRecordIndex] = { ...data[existingRecordIndex], ...updatedRecord };
                logger.debug('Updated existing centre record:', updatedRecord.id);
            } else {
                // Add new record
                data.push(updatedRecord);
                logger.debug('Added new centre record:', updatedRecord.id);
            }

            Eventbus.emit(events.CENTRES_UPDATED, [...data]); // Emit updated data
        } catch (error) {
            logger.error('Error updating centre record:', error, updatedRecord);
        }
    }

    function syncWinzCentre(centreId) {
        return api.syncWinzCentre(centreId)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    logger.warn('Centre sync cancelled:', centreId);
                    return response;
                }
            });
    }



    function loadData(forceRefresh = false) {
        // Prevent duplicate requests
        if (isLoading && !forceRefresh) {
            return Promise.resolve([...data]);
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            isLoading = false; // Ensure loading state is reset when returning cached data
            Eventbus.emit(events.CENTRES_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        isLoading = true;

        // Otherwise fetch fresh data from the API
        return api
            .getWinzCentres()
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return data; // Return existing data on cancellation
                }
                data = response || []; // Store the response data in the data variable
                Eventbus.emit(events.CENTRES_LOADED, [...data]);

                // Only register the event listener once
                if (!isListenerRegistered) {
                    Eventbus.on(events.SIGNALR.WINZ_CENTRE_DATA_CHANGES, updateCentre);
                    isListenerRegistered = true;
                    logger.debug('SignalR centre data changes listener registered');
                }

                return [...data];
            })
            .catch((error) => {
                logger.error('Error fetching centre records:', error);
                return [];
            })
            .finally(() => {
                isLoading = false;
            });
    }

    function init() {
        return loadData(true);
    }

    function clear() {
        if (isListenerRegistered) {
            Eventbus.off(updateCentre);
            isListenerRegistered = false;
            logger.debug('SignalR centre data changes listener removed');
        }
        data = [];
    }

    function updateCentreRecord(id, updatedData) {
        const existingRecord = data.find(record => record.id === id);
        if (!existingRecord) {
            logger.warn('Centre record not found for update:', id);
            return Promise.reject(new Error('Centre record not found'));
        }
        return api.updateWinzCentre(id, { ...existingRecord, ...updatedData })
            .then((response) => {
                Eventbus.emit(events.SIGNALR.WINZ_CENTRE_DATA_CHANGES, response);
                return response;

            })
            .catch((error) => {
                logger.error('Error updating centre:', error);
                throw error;
            });
    }

    function deleteCentre(id) {
        return api.deleteWinzCentre(id)
            .then((response) => {
                // Remove from local cache immediately
                const index = data.findIndex(record => record.id === id);
                if (index !== -1) {
                    data.splice(index, 1);
                    Eventbus.emit(events.CENTRES_UPDATED, [...data]);
                }
                return response;
            })
            .catch((error) => {
                logger.error('Error deleting centre:', error);
                throw error;
            });
    }

    function createCentre(centreData) {
        if (!centreData) {
            logger.warn('Invalid centre data for creation:', centreData);
            return Promise.reject(new Error('Invalid centre data'));
        }
        logger.debug('Creating centre:', centreData);
        return api.createWinzCentre(centreData)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    logger.warn('Centre creation cancelled:', centreData);
                    return response;
                }
                // Optionally, update local cache and emit event
                data.push(response);
                Eventbus.emit(events.CENTRES_UPDATED, [...data]);
                logger.debug('Centre created successfully:', response);
                return response;
            })
            .catch((error) => {
                logger.error('Error creating centre:', error, centreData);
                throw error;
            });
    }

    function getLoadingState() {
        return isLoading;
    }

    return {
        getData,
        loadData,
        init,
        clear,
        updateCentre, // For SignalR updates
        updateCentreRecord,
        deleteCentre,
        getLoadingState, // Export loading state
        createCentre, // Export createCentre
        syncWinzCentre
    };
})();

export default CentresService;
